// src/api/client.js

const USE_COOKIES_DEFAULT = false; // JWT header by default (no cookies)

// Env override (ignored if it points to *.onrender.com)
const RAW_BASE_ENV =
  (process.env.REACT_APP_API_URL || "").trim() ||
  (process.env.REACT_APP_API_BASE_URL || "").trim() ||
  (process.env.NEXT_PUBLIC_API_BASE_URL || "").trim();

// Compute a sensible default base from the current browser hostname.
// - dev.propelhabitpoints.org -> api-dev.propelhabitpoints.org
// - otherwise -> api.propelhabitpoints.org
function defaultBaseFromWindow() {
  try {
    if (typeof window !== "undefined" && window.location && window.location.hostname) {
      const h = (window.location.hostname || "").toLowerCase();
      // exact dev host (most strict / safest)
      if (h === "dev.propelhabitpoints.org") return "https://api-dev.propelhabitpoints.org";
      // also support any dev-like subdomain if you ever add more (e.g., dev2.)
      if (h.startsWith("dev.")) return "https://api-dev.propelhabitpoints.org";
    }
  } catch {}
  return "https://api.propelhabitpoints.org";
}

const HARD_DEFAULT_BASE = defaultBaseFromWindow();
const RAW_BASE = RAW_BASE_ENV && /onrender\.com/i.test(RAW_BASE_ENV) ? "" : RAW_BASE_ENV;

function norm(s = "") { return (s || "").trim().replace(/\/+$/, ""); }
const BASE = norm(RAW_BASE || HARD_DEFAULT_BASE);

// ---- token storage ----
const ACCESS_KEY = "ph_tokens_access";
const REFRESH_KEY = "ph_tokens_refresh";

// Remove old empty placeholders once per boot (helps recover from earlier builds)
try {
  if (localStorage.getItem(ACCESS_KEY) === "") localStorage.removeItem(ACCESS_KEY);
  if (localStorage.getItem(REFRESH_KEY) === "") localStorage.removeItem(REFRESH_KEY);
} catch {}

// Robustly read a token value from localStorage (plain string or JSON)
function readTokenFromStorage(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return "";
    // If it parses as JSON, try common fields
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object") {
        const candidate =
          parsed.access ??
          parsed.access_token ??
          parsed.token ??
          parsed.jwt ??
          parsed.accessToken ??
          parsed.value ??
          parsed.key ??
          "";
        return typeof candidate === "string" ? candidate : "";
      }
    } catch {
      // raw is not JSON; use as-is
    }
    return raw;
  } catch {
    return "";
  }
}

export function getAccessToken() {
  return readTokenFromStorage(ACCESS_KEY);
}
export function getRefreshToken() {
  return readTokenFromStorage(REFRESH_KEY);
}

// We accept any non-empty, non-JSON string; backend validates shape
function looksLikeUsableToken(t) {
  if (!t || typeof t !== "string") return false;
  const s = t.trim();
  if (!s) return false;
  if (s.startsWith("{")) return false; // probably JSON mistakenly stored
  return true;
}

// Extract tokens from various backend shapes
function tokensFrom(src = {}) {
  const access =
    src.access ??
    src.access_token ??
    src.token ??
    src.jwt ??
    src.accessToken ??
    src.key ??
    "";
  const refresh =
    src.refresh ??
    src.refresh_token ??
    src.refreshToken ??
    "";
  return { access: access || "", refresh: refresh || "" };
}

export function setTokens(tokens) {
  const { access, refresh } = tokensFrom(tokens);
  try {
    if (access)  localStorage.setItem(ACCESS_KEY, access);
    if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
  } catch {}
}

export function clearTokens() {
  try {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  } catch {}
}

// ---- helpers ----
function friendlyNetworkError(e) {
  const msg = (e && e.message) ? e.message : String(e || "Network error");
  if (/Failed to fetch|NetworkError|TypeError/i.test(msg))
    return "Network error. If this persists, it's likely CORS/SSL or the API host.";
  return msg;
}
async function parseJsonSafe(res) {
  const text = await res.text().catch(() => "");
  try { return text ? JSON.parse(text) : {}; } catch { return {}; }
}

/**
 * IMPORTANT (Django/DRF):
 * - Always prefer trailing slashes for endpoints, especially POST requests.
 * - Without trailing slash, APPEND_SLASH can redirect and lose request body.
 *
 * This normalizer makes the client robust even if a caller forgets the slash.
 */
function normalizeApiPath(path) {
  const raw = String(path || "");
  if (!raw) return "/";

  // Keep absolute URLs intact
  if (/^https?:\/\//i.test(raw)) return raw;

  // Split off any query/hash just in case (we also support opts.query separately)
  const noHash = raw.split("#")[0];
  const [p0, qs] = noHash.split("?");
  const p = p0.startsWith("/") ? p0 : `/${p0}`;

  // Only auto-append slash for API routes
  if (p.startsWith("/api/")) {
    // If it already ends with slash, leave it
    if (p.endsWith("/")) return qs ? `${p}?${qs}` : p;

    // If it looks like a file path, don't force a slash
    if (/\.[a-z0-9]+$/i.test(p)) return qs ? `${p}?${qs}` : p;

    return qs ? `${p}/?${qs}` : `${p}/`;
  }

  // Non-api routes unchanged
  return qs ? `${p}?${qs}` : p;
}

function makeUrl(path, opts = {}) {
  const normalizedPath = normalizeApiPath(path);
  const qs = opts.query ? `?${new URLSearchParams(opts.query).toString()}` : "";

  // If normalizeApiPath already included a "?" (rare), don't double-add
  if (normalizedPath.includes("?") && qs) {
    return `${BASE}${normalizedPath}&${qs.slice(1)}`;
  }
  return `${BASE}${normalizedPath}${qs}`;
}

const AUTH_SCHEMES = ["Bearer", "Token", "JWT"];

// Small debug helper (toggle to false if you want silence)
const DEBUG = true;
function dbg(...args) {
  if (DEBUG && typeof console !== "undefined" && console.debug) console.debug(...args);
}

// Wrap fetch
async function rawFetch(url, options) {
  let res;
  try {
    res = await fetch(url, options);
  } catch (e) {
    return { ok: false, status: 0, data: { message: friendlyNetworkError(e) }, headers: new Headers() };
  }
  const data = await parseJsonSafe(res);
  return { ok: res.ok, status: res.status, data, headers: res.headers };
}

/**
 * IMPORTANT (Django/DRF):
 * - Always prefer trailing slashes for endpoints, especially POST requests.
 * - Without trailing slash, APPEND_SLASH can redirect and lose request body.
 */

/** Try a list of refresh endpoints & payload shapes (DRF/SimpleJWT variants) */
const REFRESH_CANDIDATES = [
  // Refactored backend (preferred)
  { url: "/api/auth/refresh/", key: "refresh" },
  { url: "/api/auth/refresh/", key: "refresh_token" },

  // Legacy / generic fallbacks
  { url: "/api/token/refresh/", key: "refresh" },
  { url: "/api/token/refresh", key: "refresh" },
  { url: "/api/auth/jwt/refresh/", key: "refresh" },

  { url: "/api/token/refresh/", key: "refresh_token" },
  { url: "/api/token/refresh", key: "refresh_token" },
  { url: "/api/auth/jwt/refresh/", key: "refresh_token" },

  // Extra fallback in case something is still deployed without slash
  { url: "/api/auth/refresh", key: "refresh" },
  { url: "/api/auth/refresh", key: "refresh_token" },
];

async function tryRefresh() {
  const refresh = getRefreshToken();
  if (!looksLikeUsableToken(refresh)) {
    dbg("[client] refresh: no refresh token available");
    return { ok: false, status: 0, data: { message: "No refresh token" } };
  }

  for (const cand of REFRESH_CANDIDATES) {
    const url = makeUrl(cand.url);
    const payload = { [cand.key]: refresh };
    const res = await rawFetch(url, {
      method: "POST",
      credentials: "omit",
      headers: new Headers({ "Accept": "application/json", "Content-Type": "application/json" }),
      body: JSON.stringify(payload),
    });

    if (res.ok && res.data) {
      const { access, refresh: newRefresh } = tokensFrom(res.data);
      if (access || newRefresh) {
        setTokens({ access, refresh: newRefresh || refresh });
        dbg("[client] refresh: success via", cand.url, "received access?", !!access, "refresh?", !!newRefresh);
        return res;
      }
    }
    // Some backends send 401/403 with error bodies; keep trying the next candidate
  }

  dbg("[client] refresh: all candidates failed");
  return { ok: false, status: 401, data: { message: "Refresh failed" } };
}

// Detects auth errors from status or body
function isAuthError(res) {
  if (!res) return false;
  if (res.status === 401 || res.status === 403) return true;
  const detail = (res.data && (res.data.detail || res.data.message || res.data.error)) || "";
  if (typeof detail === "string") {
    const d = detail.toLowerCase();
    if (d.includes("signature has expired")) return true;
    if (d.includes("token_not_valid")) return true;
    if (d.includes("not authenticated")) return true;
    if (d.includes("credentials were not provided")) return true;
    if (d.includes("invalid token")) return true;
  }
  return false;
}

/**
 * Core request function.
 * - Adds Authorization header with current token (tries Bearer, Token, JWT) if token exists and auth !== false.
 * - Falls back to cookie session if configured.
 * - On auth error (401/403/expired/invalid), tries a single token refresh and retries once.
 */
async function request(path, {
  method="GET",
  headers={},
  body,
  auth=true,
  credentialsMode="omit",
  form=false,
  query,
  _noRefresh=false,        // internal guard to avoid refresh loops
} = {}) {
  const url = makeUrl(path, { query });
  const h = new Headers(headers);

  if (!form) {
    if (!h.has("Accept")) h.set("Accept", "application/json");
    if (body && !h.has("Content-Type")) h.set("Content-Type", "application/json");
  }

  const tok = getAccessToken();
  const haveToken = looksLikeUsableToken(tok);
  const wantCookies = USE_COOKIES_DEFAULT;

  const optsBase = {
    method,
    headers: h,
    credentials: wantCookies ? "include" : credentialsMode, // default cookie behavior
  };
  if (body !== undefined) {
    optsBase.body = form ? body : (typeof body === "string" ? body : JSON.stringify(body));
  }

  let lastAuthRes = null;

  // If we want auth and have a token, try header schemes first
  if (auth && haveToken) {
    for (const scheme of AUTH_SCHEMES) {
      const opts = { ...optsBase, headers: new Headers(h) };
      opts.headers.set("Authorization", `${scheme} ${tok}`);

      const r = await rawFetch(url, opts);
      if (r.ok) return r;

      lastAuthRes = r;

      if (!isAuthError(r)) {
        // 404/500/etc. → return as-is (no refresh)
        return r;
      }

      // Auth error; try next scheme
    }
  }

  // Try cookie-based session only if not already using include
  if (!wantCookies) {
    const opts = { ...optsBase, headers: new Headers(h), credentials: "include" };
    const rc = await rawFetch(url, opts);
    if (rc.ok) return rc;

    // If still auth error and we haven't refreshed yet, try refresh
    if (isAuthError(rc) && !_noRefresh) {
      const rr = await tryRefresh();
      if (rr.ok) {
        // retry original request once with new token (auth headers will be set above)
        return request(path, {
          method,
          headers,
          body,
          auth,
          credentialsMode,
          form,
          query,
          _noRefresh: true,
        });
      }
    }
    return rc;
  }

  // If we reached here, either auth=false, or no usable token.
  const r0 = await rawFetch(url, optsBase);
  if (r0.ok) return r0;

  const candidate = lastAuthRes || r0;

  // If auth error and we haven't tried refresh yet, do it.
  if (isAuthError(candidate) && !_noRefresh && haveToken) {
    const rr = await tryRefresh();
    if (rr.ok) {
      return request(path, {
        method,
        headers,
        body,
        auth,
        credentialsMode,
        form,
        query,
        _noRefresh: true,
      });
    }
  }

  return candidate;
}

// ---- GENERIC CLIENT (named export) ----
export const api = {
  get:   (p, o = {}) => request(p, { ...o, method: "GET" }),
  post:  (p, b, o = {}) => request(p, { ...o, method: "POST", body: b }),
  put:   (p, b, o = {}) => request(p, { ...o, method: "PUT", body: b }),
  patch: (p, b, o = {}) => request(p, { ...o, method: "PATCH", body: b }),
  del:   (p, o = {}) => request(p, { ...o, method: "DELETE" }),
  // Alias for code paths that call api.delete(...). Using a quoted key to avoid reserved-word issues.
  "delete": (p, o = {}) => request(p, { ...o, method: "DELETE" }),
  postForm: (p, fd, o = {}) => request(p, { ...o, method: "POST", body: fd, form: true }),
};

// ---- Auth helpers ----
export const authApi = {
  // Backend refactor: signup request lives here now
  signup: (payload) =>
    api.post("/api/auth/signup-request/", payload, { auth: false, credentialsMode: "omit" }),

  // Try email-first; if 400/401, try username payload
  login:   async (emailOrUsername, password) => {
    let res = await request("/api/auth/login/", {
      method: "POST",
      body: { email: emailOrUsername, password },
      auth: false,
      credentialsMode: "omit",
    });

    if (!res.ok && (res.status === 400 || res.status === 401)) {
      res = await request("/api/auth/login/", {
        method: "POST",
        body: { username: emailOrUsername, password },
        auth: false,
        credentialsMode: "omit",
      });
    }

    if (res.ok && res.data) {
      const { access, refresh } = tokensFrom(res.data);
      if (access || refresh) setTokens({ access, refresh });
    }
    return res;
  },

  refresh: async () => {
    const res = await tryRefresh();
    return res;
  },

  me: () => api.get("/api/auth/me/"),

  logout: () => { clearTokens(); return { ok: true, status: 200, data: {} }; },
};

// ---- Feature APIs ----
export const reportsApi = {
  // UPDATED: allow passing query params (e.g., { since, until, days })
  summary: (params = {}) => api.get("/api/reports/", { query: params }),

  // Students-by-points report used by the Reports page
  // Usage: reportsApi.pointsByStudent({ /* optional filters */ })
  pointsByStudent: (params = {}) =>
    api.get("/api/reports/", { query: { type: "points_by_student", ...params } }),

  // Individual award events (who/when/how much/why) - used by the "Points
  // I've Awarded" filter. Pass { awarded_by_me: "true" } to scope to the
  // logged-in educator, or { created_by_id } (admin only) for another one.
  pointsAwarded: (params = {}) =>
    api.get("/api/reports/", { query: { type: "points_awarded", ...params } }),

  exportCsv: (payload) => api.post("/api/reports/export/", payload),
};

export const storeApi = {
  listItems: (params = {}) => api.get("/api/store/items/", { query: params }),
  // store open/close status (scholar gets single-school; educator/staff gets multi,
  // or single-school when passing { scholar_id } to shop on a scholar's behalf)
  getStatus: (params = {}) => api.get("/api/store/status/", { query: params }),
  // Backend refactor: purchases are created via /create/
  purchase:  (payload = {}) => api.post("/api/store/purchases/create/", payload),

  // NEW: list purchases for the current user (scholar sees own; staff/educator may
  // pass { scholar_id } to see one scholar's purchases)
  listPurchases: (params = {}) => api.get("/api/store/purchases/", { query: params }),

  // Special "incentive day" style events
  // Scholar: returns { event } (or { event: null }); educator/staff: returns { events: [...] },
  // or { event } when passing { scholar_id } to shop on a scholar's behalf
  listEvents: (params = {}) => api.get("/api/store/events/", { query: params }),
  // Earned-in-window / spent / remaining budget for a specific event. Scholars call this
  // for themselves; staff/educators pass { scholar_id } to view a specific scholar's budget.
  getEventBalance: (eventId, params = {}) => api.get(`/api/store/events/${eventId}/balance/`, { query: params }),

  // Staff/educator: event purchase roster (who bought what, by room)
  getEventPurchases: (eventId) => api.get(`/api/reports/store/event/${eventId}/purchases/`),

  // Downloads the CSV export for an event's purchases. This can't be a plain
  // <a href> because auth is a JWT bearer header, not a cookie — so we fetch it
  // with the token attached and save the response as a file.
  downloadEventPurchasesCsv: async (eventId, filename = "event_purchases.csv") => {
    const tok = getAccessToken();
    const url = makeUrl(`/api/reports/store/event/${eventId}/purchases/export/`);
    const res = await fetch(url, {
      headers: tok ? { Authorization: `Bearer ${tok}` } : {},
    });
    if (!res.ok) {
      return { ok: false, status: res.status, data: await parseJsonSafe(res) };
    }
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    return { ok: true, status: res.status };
  },
};

// NEW: student-related endpoints needed for store affordability + updated balance display
export const studentsApi = {
  // Returns current user's point balance summary (used to determine what they can afford)
  // NOTE: This endpoint must exist in the backend; we’ll use it in Store.js.
  myBalance: () => api.get("/api/students/me/balance/"),

  // Educator/staff: a specific scholar's point balance (used to shop on their behalf).
  balanceFor: (scholarId) => api.get(`/api/students/${scholarId}/balance/`),
};
